import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/** Emitted saat backend return 401 → AuthContext dengar event ini untuk clear token + redirect.
 *  Dispatch via window agar tidak coupling ke React hook. */
export const AUTH_LOGOUT_EVENT = "hris-auth-401";
/** Emitted saat token login diganti tanpa login ulang (ganti password sendiri)
 *  → AuthContext menghitung ulang batas sesi pukul 00.00 WIB dari token baru. */
export const TOKEN_DIGANTI_EVENT = "hris-token-diganti";

// Tanda perangkat (keputusan owner 24 Sep 2026): UUID acak yang dibuat SEKALI
// per browser, dikirim di setiap permintaan sebagai `X-Perangkat-Id`. Server
// memakainya untuk mengunci reset password lupa-password PER PERANGKAT (20x
// salah kode mengunci perangkat ini saja, dan hanya login dari perangkat ini
// yang membukanya) — lihat `backend/routers/auth.py::perangkat_dari`.
// Tanda ini BUKAN rahasia dan bukan bukti identitas; tanpa tanda, server
// memakai IP sebagai gantinya.
const KUNCI_PERANGKAT = "epplehub_perangkat_id";
let perangkatDiMemori = null;

function uuidAcak() {
  // `crypto.randomUUID` hanya ada di secure context (https / localhost). Toko
  // yang membuka aplikasi lewat http://IP-LAN (start-lokal.ps1 -Lan) tidak
  // punya fungsi itu, tapi `getRandomValues` tetap ada — UUID v4 disusun sendiri
  // dengan bentuk yang sama persis (server hanya menerima bentuk v4).
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export function idPerangkat() {
  if (perangkatDiMemori) return perangkatDiMemori;
  try {
    const ada = localStorage.getItem(KUNCI_PERANGKAT);
    if (ada) { perangkatDiMemori = ada; return ada; }
  } catch (_e) { /* penyimpanan diblokir (mode privat) — pakai tanda sesaat di memori */ }
  try {
    perangkatDiMemori = uuidAcak();
  } catch (_e) {
    return null;  // tanpa sumber acak sama sekali: server memakai IP
  }
  try { localStorage.setItem(KUNCI_PERANGKAT, perangkatDiMemori); } catch (_e) { /* lihat di atas */ }
  return perangkatDiMemori;
}

/** Axios instance dengan:
 *  - Timeout 30 detik (cukup untuk request berat: laporan/export)
 *  - Bearer token dari localStorage + tanda perangkat
 *  - Response interceptor: hanya 401 yang trigger logout event
 *  - Retry 1x otomatis untuk network error / timeout / 5xx (transient) */
export const api = axios.create({
  baseURL: API,
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("hris_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const perangkat = idPerangkat();
  if (perangkat) config.headers["X-Perangkat-Id"] = perangkat;
  // Mark for retry logic
  if (config._retryCount === undefined) config._retryCount = 0;
  return config;
});

/** Simpan token login baru yang diterbitkan server tanpa login ulang. */
export function simpanToken(token) {
  if (!token) return;
  localStorage.setItem("hris_token", token);
  window.dispatchEvent(new CustomEvent(TOKEN_DIGANTI_EVENT));
}

// Ganti password SENDIRI mencabut semua sesi akun ini di server — termasuk
// token perangkat ini — lalu membalas token baru untuk perangkat ini. Selama
// permintaan itu berjalan, permintaan lain dari halaman yang sama (lonceng,
// polling) bisa terlanjur membawa token lama dan dibalas 401. Tanpa penantian
// ini, 401 itu mengeluarkan perangkat yang justru baru mengganti password.
let menungguTokenBaru = null;
// token lama → token penggantinya. Permintaan yang ditolak 401 hanya diulang
// kalau tokennya ada di sini DAN penggantinya masih token yang berlaku — jadi
// permintaan milik sesi lain (mis. pengguna sebelumnya di tab yang sama) tidak
// pernah diulang atas nama pengguna yang sekarang login.
const tokenDigantikan = new Map();

/** Ganti password sendiri (`/auth/change-password`,
 *  `/auth/first-login-change-password`): token baru dari respons disimpan
 *  SEBELUM penantian 401 dilepas. Permintaan ini sendiri ditandai supaya 401
 *  miliknya (sesi sudah dicabut perangkat lain) tidak ikut menunggu dirinya. */
export function gantiPasswordSendiri(url, body) {
  const proses = (async () => {
    const lama = localStorage.getItem("hris_token");
    const res = await api.post(url, body, { _gantiPasswordSendiri: true });
    const baru = res?.data?.token;
    if (lama && baru) tokenDigantikan.set(lama, baru);
    simpanToken(baru);
    return res;
  })();
  const tunggu = proses.catch(() => null);
  menungguTokenBaru = tunggu;
  tunggu.then(() => { if (menungguTokenBaru === tunggu) menungguTokenBaru = null; });
  return proses;
}

/** Kategori error yang boleh di-retry (transient issue, bukan authz/bad request). */
function isRetryable(error) {
  // Network error (no response) OR timeout OR server error 5xx
  if (!error.response) return true;                     // network / timeout / CORS
  const status = error.response.status;
  return status >= 500 && status < 600;                  // 500-599 transient server
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config || {};
    const status = error.response?.status;

    // 401 → session invalid: clear token + notify AuthContext untuk redirect ke /login.
    // TIDAK dilakukan untuk error jaringan/timeout — user tetap login, aksi bisa diretry.
    if (status === 401) {
      // Token yang dipakai permintaan ini sudah digantikan token baru (ganti
      // password sendiri) → ulangi SEKALI dengan token baru, jangan keluarkan.
      if (menungguTokenBaru && !config._gantiPasswordSendiri) await menungguTokenBaru;
      const tokenDipakai = String(config.headers?.Authorization || "").replace(/^Bearer /, "");
      const pengganti = tokenDipakai ? tokenDigantikan.get(tokenDipakai) : null;
      if (pengganti && pengganti === localStorage.getItem("hris_token") && !config._ulangTokenBaru) {
        config._ulangTokenBaru = true;
        return api(config);
      }
      localStorage.removeItem("hris_token");
      window.dispatchEvent(new CustomEvent(AUTH_LOGOUT_EVENT, { detail: { reason: "unauthorized" } }));
      return Promise.reject(error);
    }

    // Retry sekali untuk transient error (network / timeout / 5xx).
    // Jangan retry POST/PUT/PATCH/DELETE yang bukan idempotent — bisa menyebabkan double-submit.
    const method = (config.method || "get").toLowerCase();
    const idempotent = ["get", "head", "options"].includes(method);
    if (idempotent && isRetryable(error) && (config._retryCount || 0) < 1) {
      config._retryCount = (config._retryCount || 0) + 1;
      // small backoff
      await new Promise((res) => setTimeout(res, 400));
      return api(config);
    }

    return Promise.reject(error);
  }
);

/** Optional: panggil `refreshToken()` untuk perpanjang session tanpa re-login.
 *  AuthContext memanggil ini secara periodik (~setiap 6 jam) saat user aktif. */
export async function refreshToken() {
  const token = localStorage.getItem("hris_token");
  if (!token) return null;
  try {
    const { data } = await api.post("/auth/refresh");
    if (data?.token) {
      localStorage.setItem("hris_token", data.token);
      return data.token;
    }
  } catch (_err) {
    // silent fail — kalau 401, response interceptor sudah handle logout
  }
  return null;
}

/** Ekstrak pesan + kode arsip dari respons gagal server — SATU sumber dipakai
 *  Sales.jsx (create_sale) dan InputNota.jsx (approve nota), dua titik yang
 *  memicu `post_sale_to_ledger` (kontrak G1 keputusan owner #1). Saat posting
 *  stok/jurnal gagal, backend TIDAK menyimpan dokumennya dan menulis arsip
 *  kegagalan ke `sales_gagal` supaya bisa ditelusuri — toast generik "Gagal"
 *  membuang jejak itu, jadi pesan server + `arsip_id` WAJIB ikut ditampilkan.
 *  Toleran terhadap beberapa bentuk respons (`detail` string biasa, `detail`
 *  objek {message, arsip_id}, atau `arsip_id` di level atas) karena bentuk
 *  pastinya ditentukan sisi backend yang dikerjakan paralel. */
export function pesanGagalDariServer(e, fallback = "Gagal menyimpan") {
  const data = e?.response?.data;
  const detail = data?.detail;
  const pesan = typeof detail === "string"
    ? detail
    : (detail?.message || detail?.detail || data?.message || fallback);
  const arsipId = data?.arsip_id || detail?.arsip_id;
  return arsipId ? `${pesan} — kode arsip: ${arsipId}` : pesan;
}

export const idr = (n) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(n || 0);

export const fmtNum = (n) => new Intl.NumberFormat("id-ID").format(n || 0);

/** Short currency for tight spaces / mobile podium cards. Rp 48,3jt / Rp 500rb / Rp 750 */
export const idrShort = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000_000) return "Rp " + (v / 1_000_000_000).toFixed(1).replace(".0", "") + "M";
  if (v >= 1_000_000) return "Rp " + (v / 1_000_000).toFixed(1).replace(".0", "") + "jt";
  if (v >= 1_000) return "Rp " + Math.round(v / 1_000) + "rb";
  return "Rp " + Math.round(v);
};

/**
 * Pesan galat yang AMAN dirender React.
 *
 * Ditemukan 11 Sep 2026 saat verifikasi browser: `toast.error(e?.response?.data?.detail)`
 * MENGHANCURKAN halaman kalau backend membalas **422** — FastAPI mengirim
 * `detail` sebagai ARRAY OBJEK (`{type, loc, msg, input, ctx}`), dan React
 * menolak merendernya ("Objects are not valid as a React child"), sehingga
 * seluruh pohon komponen unmount → layar putih. Terbukti di halaman Login
 * dengan email berdomain khusus (`@sesuatu.local`) yang lolos validasi
 * `type="email"` browser tapi ditolak `EmailStr` di server.
 *
 * Pola `.data?.detail` dipakai 559 kali di 119 berkas, jadi ini bukan masalah
 * satu halaman. Helper ini yang dipakai di tempat yang sudah terbukti kena dan
 * di halaman baru; penyisiran sisanya pekerjaan tersendiri.
 */
export function pesanGalat(err, baku = "Terjadi kesalahan") {
  const d = err?.response?.data?.detail;
  if (typeof d === "string" && d.trim()) return d;
  if (Array.isArray(d)) {
    const pesan = d
      .map((x) => (typeof x === "string" ? x : x?.msg))
      .filter(Boolean)
      .join(", ");
    if (pesan) return pesan;
  }
  if (d && typeof d === "object" && typeof d.msg === "string") return d.msg;
  return baku;
}
